<template>
    <div data-testid="step-riskAssessment" class="container">
        <h1 class="container__standard-header">
            {{ t(`${TRANSLATION_KEY}.riskAssessmentHeader`) }}
        </h1>

        <form @submit.prevent="null">
            <div
                class="risk-assessment-description fz-12"
                v-html="t(`${TRANSLATION_KEY}.questionnaireDescription`)"
            />

            <div class="row custom-select-wrapper">
                <Select
                    name="employmentStatus"
                    :item-list="employmentStatusOptions"
                    :label="t(`${TRANSLATION_KEY}.employmentStatus`)"
                    :placeholder="t(`${TRANSLATION_KEY}.employmentStatus`)"
                    @update:model-value="formData.employmentStatus = $event"
                />
            </div>

            <div
                v-if="
                    formData.employmentStatus === 'EMPLOYED' ||
                    formData.employmentStatus === 'SELF_EMPLOYED' ||
                    formData.employmentStatus === 'BUSINESS_OWNER' ||
                    formData.employmentStatus === 'STUDENT_WITH_EMPLOYMENT'
                "
            >
                <div class="row custom-select-wrapper">
                    <Select
                        name="natureOfBusiness"
                        :item-list="natureOfBusinessOptions"
                        :label="t(`${TRANSLATION_KEY}.natureOfBusiness`)"
                        :placeholder="t(`${TRANSLATION_KEY}.natureOfBusiness`)"
                        @update:model-value="formData.natureOfBusiness = $event"
                    />
                </div>
                <div class="row custom-select-wrapper">
                    <Select
                        name="position"
                        :item-list="roleOptions"
                        :label="t(`${TRANSLATION_KEY}.role`)"
                        :placeholder="t(`${TRANSLATION_KEY}.role`)"
                        @update:model-value="formData.position = $event"
                    />
                </div>
            </div>

            <div class="row custom-select-wrapper mb-0">
                <Select
                    name="sourceOfFunds"
                    :item-list="sourceOfFundsOptions"
                    :label="t(`${TRANSLATION_KEY}.sourceOfFunds`)"
                    :placeholder="t(`${TRANSLATION_KEY}.sourceOfFunds`)"
                    @update:model-value="formData.sourceOfFunds = $event"
                />
            </div>
            <div class="risk-assessment-description">
                {{ t(`${TRANSLATION_KEY}.sourceOfFundsDisclaimer`) }}
            </div>

            <div class="row custom-select-wrapper mb-0">
                <Select
                    name="income"
                    :item-list="incomeOptions"
                    :label="t(`${TRANSLATION_KEY}.income`)"
                    :placeholder="t(`${TRANSLATION_KEY}.income`)"
                    @update:model-value="formData.income = $event"
                />
            </div>
            <div class="risk-assessment-description">
                {{ t(`${TRANSLATION_KEY}.incomeDisclaimer`) }}
            </div>

            <div class="row custom-select-wrapper mb-0">
                <Select
                    name="savings"
                    :item-list="savingsOptions"
                    :label="t(`${TRANSLATION_KEY}.savings`)"
                    :placeholder="t(`${TRANSLATION_KEY}.savings`)"
                    @update:model-value="formData.savings = $event"
                />
            </div>
            <div class="risk-assessment-description">
                {{ t(`${TRANSLATION_KEY}.savingsDisclaimer`) }}
            </div>

            <SubmitButton
                :action="Action.RISK_ASSESSMENT"
                :button-value="t(`${TRANSLATION_KEY}.next`)"
                :data="formData"
                data-testid="btn-riskAssessment-nextStep"
                class="button button--submit"
                :disabled="!formIsValid"
                @click="handleNextStep"
            />
        </form>
    </div>
</template>

<script lang="ts">
import {
    defineComponent,
    ref,
    reactive,
    computed,
    onMounted,
    onUnmounted,
    watch,
} from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { SET_PROGRESS } from '@/constants/mutation-types';

import {
    EmploymentStatus,
    NatureOfBusiness,
    Role,
    SourceOfFunds,
    Income,
    Savings,
} from '@/constants/form-values';
import { Action } from '@/constants/ajax';

import type { FieldInterface } from '@/types';
import Select from '@/components/basic/fields/Select.vue';
import SubmitButton from '@/components/basic/buttons/SubmitButton.vue';
import Popup from '@/components/basic/Popup.vue';
import {
    AmplitudeAnalyticsEvents,
    amplitudeSendEvent,
} from '@/utils/AmplitudeAnalyticsEvents';
import { PROGRESS_BAR_TIMES } from '@/constants/progress-bar-times';

export default defineComponent({
    name: 'RiskAssessment',
    components: {
        Select,
        SubmitButton,
        Popup,
    },
    setup() {
        const store = useStore();
        const { t } = useI18n();

        const formData = reactive({
            employmentStatus: '',
            natureOfBusiness: '',
            position: '',
            sourceOfFunds: '',
            income: '',
            savings: '',
        });

        const TRANSLATION_KEY = 'steps.RiskAssessment';
        const formIsValid = ref(false);

        const isValid = () => {
            const customSelects = document.querySelectorAll(
                '.custom-select-wrapper .custom-select'
            );
            const selectsLength = customSelects.length;
            let validSelectsLength = 0;

            customSelects.forEach((select) => {
                if (select.classList.contains('valid')) {
                    validSelectsLength++;
                }
            });
            formIsValid.value = validSelectsLength === selectsLength;
        };

        onMounted(() => {
            store.commit(SET_PROGRESS, PROGRESS_BAR_TIMES.RISK_ASSESSMENT);

            isValid();

            const intervalId = setInterval(isValid, 100);

            onUnmounted(() => {
                clearInterval(intervalId);
            });
        });

        const employmentStatusOptions = computed<FieldInterface[]>(() => [
            {
                key: EmploymentStatus.EMPLOYED,
                value: t('steps.RiskAssessment.employed'),
            },
            {
                key: EmploymentStatus.SELF_EMPLOYED,
                value: t('steps.RiskAssessment.selfEmployed'),
            },
            {
                key: EmploymentStatus.BUSINESS_OWNER,
                value: t('steps.RiskAssessment.businessOwner'),
            },
            {
                key: EmploymentStatus.UNEMPLOYED,
                value: t('steps.RiskAssessment.unemployed'),
            },
            {
                key: EmploymentStatus.RETIRED,
                value: t('steps.RiskAssessment.retired'),
            },
            {
                key: EmploymentStatus.STUDENT_EMPLOYED,
                value: t('steps.RiskAssessment.studentEmployed'),
            },
            {
                key: EmploymentStatus.STUDENT_UNEMPLOYED,
                value: t('steps.RiskAssessment.studentUnemployed'),
            },
        ]);

        const natureOfBusinessOptions = computed<FieldInterface[]>(() => [
            {
                key: NatureOfBusiness.AGRICULTURAL,
                value: t('steps.RiskAssessment.businessAgricultural'),
            },
            {
                key: NatureOfBusiness.EDUCATION,
                value: t('steps.RiskAssessment.businessEducation'),
            },
            {
                key: NatureOfBusiness.SOCIAL_SERVICES_HEALTH,
                value: t('steps.RiskAssessment.businessSocialServices'),
            },
            {
                key: NatureOfBusiness.FINANCIAL_LEGAL_ACCOUNTING,
                value: t(
                    'steps.RiskAssessment.businessFinancialLegalAccounting'
                ),
            },
            {
                key: NatureOfBusiness.RETAIL_SERVICES,
                value: t('steps.RiskAssessment.businessRetailServices'),
            },
            {
                key: NatureOfBusiness.ADMINISTRATIVE,
                value: t('steps.RiskAssessment.businessAdministrative'),
            },
            {
                key: NatureOfBusiness.INSURANCE_SERVICE,
                value: t('steps.RiskAssessment.businessInsuranceService'),
            },
            {
                key: NatureOfBusiness.ENGINEERING_IT_TECHNOLOGY,
                value: t(
                    'steps.RiskAssessment.businessEngineeringItTechnology'
                ),
            },
            {
                key: NatureOfBusiness.RESTAURANTS,
                value: t('steps.RiskAssessment.businessRestaurants'),
            },
            {
                key: NatureOfBusiness.TRANSPORT_LOGISTICS,
                value: t('steps.RiskAssessment.businessTransportLogistics'),
            },
            {
                key: NatureOfBusiness.AUTOMOTIVE,
                value: t('steps.RiskAssessment.businessAutomotive'),
            },
            {
                key: NatureOfBusiness.CASINO_BETTING,
                value: t('steps.RiskAssessment.businessCasinoBettings'),
            },
            {
                key: NatureOfBusiness.NON_PROFIT_CHARITY,
                value: t('steps.RiskAssessment.businessNonProfitCharity'),
            },
            {
                key: NatureOfBusiness.DEFENCE_MILITARY_AEROSPACE,
                value: t(
                    'steps.RiskAssessment.businessDefenceMilitaryAerospace'
                ),
            },
            {
                key: NatureOfBusiness.LEISURE_TOURISM,
                value: t('steps.RiskAssessment.businessLeisureTourism'),
            },
            {
                key: NatureOfBusiness.MINING_NATURAL_RESOURCES_OIL,
                value: t(
                    'steps.RiskAssessment.businessMiningNaturalResourcesOil'
                ),
            },
            {
                key: NatureOfBusiness.PHARMACEUTICALS_BIOTECH,
                value: t('steps.RiskAssessment.businessPharmaceuticalsBiotech'),
            },
            {
                key: NatureOfBusiness.PROPERTY_CONSTRUCTION,
                value: t('steps.RiskAssessment.businessPropertyConstruction'),
            },
            {
                key: NatureOfBusiness.EXPORT_IMPORT,
                value: t('steps.RiskAssessment.businessExportImport'),
            },
            {
                key: NatureOfBusiness.SALE_DISTRIBUTION_ALCOHOL_TOBACCO,
                value: t('steps.RiskAssessment.businessSaleAlcoholTobacco'),
            },
            {
                key: NatureOfBusiness.MANUFACTURING,
                value: t('steps.RiskAssessment.businessManufacturing'),
            },
            {
                key: NatureOfBusiness.MARKETING,
                value: t('steps.RiskAssessment.businessMarketing'),
            },
        ]);

        const roleOptions = computed<FieldInterface[]>(() => [
            { key: Role.JUNIOR, value: t('steps.RiskAssessment.junior') },
            { key: Role.MID, value: t('steps.RiskAssessment.mid') },
            { key: Role.SENIOR, value: t('steps.RiskAssessment.senior') },
            {
                key: Role.MANAGER_DIRECTOR,
                value: t('steps.RiskAssessment.managerDirector'),
            },
            {
                key: Role.CONTRACTOR,
                value: t('steps.RiskAssessment.contractor'),
            },
        ]);

        const sourceOfFundsOptions = computed<FieldInterface[]>(() => {
            const baseValues: FieldInterface[] = [
                {
                    key: SourceOfFunds.EMPLOYMENT_INCOME,
                    value: t('steps.RiskAssessment.employmentIncome'),
                },
                {
                    key: SourceOfFunds.BUSINESS_ACTIVITY,
                    value: t('steps.RiskAssessment.businessActivity'),
                },
                {
                    key: SourceOfFunds.PENSION,
                    value: t('steps.RiskAssessment.pension'),
                },
                {
                    key: SourceOfFunds.SAVINGS_INVESTMENTS,
                    value: t('steps.RiskAssessment.savingsInvestments'),
                },
                {
                    key: SourceOfFunds.GRANTS_LOANS,
                    value: t('steps.RiskAssessment.grantsLoans'),
                },
            ];

            if (
                formData.employmentStatus === EmploymentStatus.UNEMPLOYED ||
                formData.employmentStatus === 'STUDENT_WITHOUT_EMPLOYMENT'
            ) {
                return baseValues.slice(2);
            }
            return baseValues;
        });

        const incomeOptions = computed<FieldInterface[]>(() => [
            { key: Income.EUR0, value: t('steps.RiskAssessment.bracketOne') },
            { key: Income.EUR10, value: t('steps.RiskAssessment.bracketTwo') },
            {
                key: Income.EUR16,
                value: t('steps.RiskAssessment.bracketThree'),
            },
            { key: Income.EUR30, value: t('steps.RiskAssessment.bracketFour') },
            { key: Income.EUR60, value: t('steps.RiskAssessment.bracketFive') },
            { key: Income.EUR100, value: t('steps.RiskAssessment.bracketSix') },
            {
                key: Income.EUR250,
                value: t('steps.RiskAssessment.bracketSeven'),
            },
        ]);

        const savingsOptions = computed<FieldInterface[]>(() => [
            { key: Savings.EUR0, value: t('steps.RiskAssessment.bracketOne') },
            { key: Savings.EUR10, value: t('steps.RiskAssessment.bracketTwo') },
            {
                key: Savings.EUR16,
                value: t('steps.RiskAssessment.bracketThree'),
            },
            {
                key: Savings.EUR30,
                value: t('steps.RiskAssessment.bracketFour'),
            },
            {
                key: Savings.EUR60,
                value: t('steps.RiskAssessment.bracketFive'),
            },
            {
                key: Savings.EUR100,
                value: t('steps.RiskAssessment.bracketSix'),
            },
            {
                key: Savings.EUR250,
                value: t('steps.RiskAssessment.bracketSeven'),
            },
        ]);

        watch(
            () => formData.employmentStatus,
            (newValue) => {
                if (
                    newValue === EmploymentStatus.UNEMPLOYED ||
                    newValue === EmploymentStatus.RETIRED ||
                    newValue === EmploymentStatus.STUDENT_UNEMPLOYED
                ) {
                    formData.natureOfBusiness = '';
                    formData.position = '';
                }
            }
        );

        const handleNextStep = () => {
            if (formData.employmentStatus) {
                amplitudeSendEvent(
                    AmplitudeAnalyticsEvents.riskAssessment.EMPLOYMENT_STATUS(
                        formData.employmentStatus
                    )
                );
            }
            amplitudeSendEvent(
                AmplitudeAnalyticsEvents.riskAssessment
                    .FINANCIAL_PROFILE_CONTINUE
            );
        };

        return {
            t,
            TRANSLATION_KEY,
            formData,
            formIsValid,
            isValid,
            employmentStatusOptions,
            natureOfBusinessOptions,
            roleOptions,
            sourceOfFundsOptions,
            incomeOptions,
            savingsOptions,
            handleNextStep,
            Action,
        };
    },
});
</script>
